import React, { useState, useEffect, useRef } from 'react'
import { messagingService, Message, MessageThread } from '../lib/messagingService'
import { useEventYear } from './EventYearContext'
import { User } from './AuthContext'

interface PressGaggleChatProps {
  currentUser: User
}

export const PressGaggleChat: React.FC<PressGaggleChatProps> = ({ currentUser }) => {
  const { currentYear } = useEventYear()
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [thread, setThread] = useState<MessageThread | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [messageAreaHeight, setMessageAreaHeight] = useState(() => {
    const saved = localStorage.getItem('press-gaggle-height')
    return saved ? parseInt(saved) : 400
  })
  const [isResizing, setIsResizing] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const subscriptionRef = useRef<any>(null)
  const resizeStartY = useRef<number>(0)
  const startHeight = useRef<number>(0)

  // Detect if user is on mobile device
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           window.innerWidth <= 768
  }

  // Scroll to bottom of messages
  const scrollToBottom = (immediate = false) => {
    console.log('PressGaggle scrollToBottom called, immediate:', immediate, 'isMobile:', isMobile())
    
    // Use longer delay for mobile to ensure DOM is ready
    const delay = isMobile() ? (immediate ? 200 : 100) : (immediate ? 100 : 50)
    
    setTimeout(() => {
      const element = messagesEndRef.current
      if (element) {
        const container = element.parentElement
        if (container) {
          // For mobile, always use direct scrollTop for reliability
          if (isMobile()) {
            container.scrollTop = container.scrollHeight + 1000 // Add extra to ensure we're at bottom
            console.log('PressGaggle mobile scroll: scrollTop set to', container.scrollHeight)
          } else {
            // Desktop can use smooth scrolling
            element.scrollIntoView({ 
              behavior: immediate ? 'instant' : 'smooth',
              block: 'end'
            })
            // Also set scrollTop as backup
            container.scrollTop = container.scrollHeight
          }
          console.log('PressGaggle scroll completed, scrollTop:', container.scrollTop, 'scrollHeight:', container.scrollHeight)
        }
      }
    }, delay)
  }

  // Handle resize start
  const handleResizeStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    setIsResizing(true)
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    resizeStartY.current = clientY
    startHeight.current = messageAreaHeight
  }

  // Handle resize move
  const handleResizeMove = (e: MouseEvent | TouchEvent) => {
    if (!isResizing) return
    
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    const deltaY = clientY - resizeStartY.current // Reversed: positive = drag down = bigger
    const newHeight = Math.max(200, Math.min(800, startHeight.current + deltaY))
    
    setMessageAreaHeight(newHeight)
  }

  // Handle resize end
  const handleResizeEnd = () => {
    if (!isResizing) return
    setIsResizing(false)
    localStorage.setItem('press-gaggle-height', messageAreaHeight.toString())
  }

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e: MouseEvent) => handleResizeMove(e)
      const handleMouseUp = () => handleResizeEnd()
      const handleTouchMove = (e: TouchEvent) => handleResizeMove(e)
      const handleTouchEnd = () => handleResizeEnd()

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleTouchMove)
      document.addEventListener('touchend', handleTouchEnd)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [isResizing, messageAreaHeight])

  // Load Press Gaggle thread and messages
  useEffect(() => {
    let mounted = true

    const loadPressGaggle = async () => {
      setIsLoading(true)
      
      // Get Press Gaggle thread
      const pressGaggleThread = await messagingService.getPressGaggleThread()
      
      if (!mounted) return
      
      if (pressGaggleThread) {
        setThread(pressGaggleThread)
        
        // Load existing messages
        const existingMessages = await messagingService.getMessagesForThread(pressGaggleThread.id, currentYear)
        
        if (mounted) {
          setMessages(existingMessages)
          
          // Mark thread as read
          await messagingService.markThreadAsRead(pressGaggleThread.id, currentUser.id)
          
          // Subscribe to real-time updates
          subscriptionRef.current = messagingService.subscribeToThread(
            pressGaggleThread.id,
            (newMessage: Message) => {
              if (mounted) {
                setMessages(prev => [...prev, newMessage])
                
                // Auto-mark as read if user is viewing
                messagingService.markThreadAsRead(pressGaggleThread.id, currentUser.id)
              }
            }
          )
        }
      }
      
      if (mounted) {
        setIsLoading(false)
      }
    }

    loadPressGaggle()

    return () => {
      mounted = false
      if (subscriptionRef.current) {
        messagingService.unsubscribe(subscriptionRef.current)
      }
    }
  }, [currentUser.id, currentYear])

  // Scroll to bottom when messages change
  useEffect(() => {
    console.log('PressGaggle messages changed, scrolling. Message count:', messages.length)
    if (messages.length > 0) {
      scrollToBottom()
    }
  }, [messages])

  // Scroll to bottom when thread loads
  useEffect(() => {
    console.log('PressGaggle thread changed. Thread:', thread?.title, 'Message count:', messages.length)
    if (thread && messages.length > 0) {
      console.log('PressGaggle triggering immediate scroll for thread entry')
      scrollToBottom(true) // Immediate scroll when entering conversation
    }
  }, [thread])

  // Additional effect to ensure scroll after all loading is complete
  useEffect(() => {
    if (!isLoading && thread && messages.length > 0) {
      console.log('PressGaggle loading complete, ensuring scroll to bottom')
      // Extra scroll attempts for mobile reliability
      if (isMobile()) {
        // Multiple scroll attempts to handle various mobile browser quirks
        scrollToBottom(true)
        setTimeout(() => scrollToBottom(true), 300)
        setTimeout(() => scrollToBottom(true), 500)
        setTimeout(() => scrollToBottom(true), 1000)
      } else {
        setTimeout(() => {
          scrollToBottom(true)
        }, 200)
      }
    }
  }, [isLoading, thread, messages.length])

  // Send message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newMessage.trim() || !thread || isSending) return
    
    setIsSending(true)
    
    try {
      const sentMessage = await messagingService.sendMessage(
        thread.id,
        currentUser.id,
        newMessage,
        false,
        currentYear
      )
      
      if (sentMessage) {
        setNewMessage('')
        // Message will be added via real-time subscription
      }
    } catch (error) {
      console.error('Error sending message:', error)
      alert('Failed to send message. Please try again.')
    } finally {
      setIsSending(false)
    }
  }

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Format date for message grouping
  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday'
    } else {
      return date.toLocaleDateString()
    }
  }

  // Group messages by date
  const groupedMessages = messages.reduce((groups: { [key: string]: Message[] }, message) => {
    const date = formatDate(message.created_at)
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(message)
    return groups
  }, {})

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading Press Gaggle...</div>
      </div>
    )
  }

  if (!thread) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Press Gaggle not available</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-white max-h-screen min-h-0">
      {/* Header */}
      <div className="flex-none p-4 border-b border-gray-200 bg-pink-50">
        <h2 className="text-xl font-semibold text-pink-700">📢 {thread.title}</h2>
        <p className="text-sm text-gray-600 mt-1">{thread.description}</p>
      </div>

      {/* Messages Area */}
      <div 
        className="overflow-y-auto p-4 space-y-4"
        style={{ height: `${messageAreaHeight}px` }}
      >
        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
          <div key={date}>
            {/* Date Separator */}
            <div className="flex items-center my-4">
              <div className="flex-1 border-t border-gray-200"></div>
              <div className="px-3 text-sm text-gray-500 bg-white">{date}</div>
              <div className="flex-1 border-t border-gray-200"></div>
            </div>

            {/* Messages for this date */}
            {dateMessages.map((message) => {
              const isOwnMessage = message.sender_id === currentUser.id
              const senderName = message.sender?.name || 'Unknown User'
              const senderRole = message.sender?.role || ''
              const senderOutlet = message.sender?.outlet || ''

              return (
                <div key={message.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg shadow ${
                    isOwnMessage 
                      ? 'bg-pink-500 text-white' 
                      : 'bg-gray-100 text-gray-900'
                  }`}>
                    {/* Sender info (only for others' messages) */}
                    {!isOwnMessage && (
                      <div className="text-xs text-gray-600 mb-1">
                        <strong>{senderName}</strong>
                        {senderRole && ['pr', 'admin'].includes(senderRole) && (
                          <span className="ml-1">(PR Team)</span>
                        )}
                        {senderRole === 'journalist' && senderOutlet && (
                          <span className="ml-1">({senderOutlet})</span>
                        )}
                      </div>
                    )}
                    
                    {/* Message content */}
                    <div className="break-words">{message.content}</div>
                    
                    {/* Timestamp */}
                    <div className={`text-xs mt-1 ${
                      isOwnMessage ? 'text-pink-100' : 'text-gray-500'
                    }`}>
                      {formatTime(message.created_at)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Resize Handle */}
      <div 
        className={`flex-none h-3 sm:h-2 border-t border-gray-200 cursor-ns-resize hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation ${isResizing ? 'bg-gray-200' : ''}`}
        onMouseDown={handleResizeStart}
        onTouchStart={handleResizeStart}
        title="Drag to resize message area"
      >
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-12 sm:w-8 h-1 sm:h-0.5 bg-gray-400 rounded"></div>
        </div>
      </div>

      {/* Message Input */}
      <div className="flex-none p-4 border-t border-gray-200 bg-gray-50">
        <form onSubmit={handleSendMessage} className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 px-3 py-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent touch-manipulation"
            disabled={isSending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || isSending}
            className="px-4 py-3 sm:py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation text-base sm:text-sm font-medium"
          >
            {isSending ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  )
}